// Tracking service for media plays and page views

class TrackingService {
    constructor() {
        this.sessionId = this.generateSessionId();
        this.playTrackers = new Map(); // Track ongoing plays
    }

    generateSessionId() {
        // Persist in sessionStorage so a page reload keeps the same session id
        // instead of silently starting a "new" session — this feeds session-based
        // dedup (media_plays) and the active-now/unique-visitor analytics.
        // Survives reloads within a tab, resets per new tab/window (correct semantics).
        try {
            const existing = sessionStorage.getItem('deepwave_session_id');
            if (existing) return existing;
            const fresh = 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
            sessionStorage.setItem('deepwave_session_id', fresh);
            return fresh;
        } catch (e) {
            // sessionStorage unavailable (private browsing, etc.) — fall back to in-memory
            return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
        }
    }

    // Track media play (audio/video)
    async trackPlay(uploadId, duration = 0, completed = false) {
        try {
            // Add debug logging to identify multiple calls
            const trackType = duration === 0 && !completed ? 'INITIAL' : 
                             completed ? 'COMPLETION' : 'PROGRESS';
            console.log(`🎵 Tracking ${trackType} for upload ${uploadId} (duration: ${duration}s, completed: ${completed})`);
            
            const response = await fetch(`/api/tracking/play/${uploadId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`,
                    'X-Session-ID': this.sessionId
                },
                body: JSON.stringify({
                    duration: Math.round(duration),
                    completed
                })
            });

            const result = await response.json();
            
            // Log if the backend actually incremented the count
            if (result.incrementedCount) {
                console.log(`✅ Play count incremented for upload ${uploadId}`);
            } else {
                console.log(`ℹ️ Progress tracked for upload ${uploadId} (no count increment)`);
            }
            
            return result;
        } catch (error) {
            console.error('Error tracking play:', error);
        }
    }

    // Track page view
    async trackPageView(pageType, pageIdentifier) {
        try {
            const response = await fetch('/api/tracking/view', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`,
                    'X-Session-ID': this.sessionId,
                    'Referer': document.referrer || ''
                },
                body: JSON.stringify({ pageType, pageIdentifier })
            });
            return await response.json();
        } catch (error) {
            console.error('Error tracking page view:', error);
        }
    }

    // Track a like or unlike action
    async trackLike(targetType, targetId, action, pagePath) {
        try {
            await fetch('/api/tracking/like', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`,
                    'X-Session-ID': this.sessionId,
                    'Referer': document.referrer || ''
                },
                body: JSON.stringify({ targetType, targetId, action, pagePath: pagePath || window.location.pathname })
            });
        } catch (error) {
            console.error('Error tracking like event:', error);
        }
    }

    // Start tracking media play with automatic duration tracking
    startPlayTracking(uploadId, mediaElement) {
        // Prevent duplicate tracking for the same upload
        if (this.playTrackers.has(uploadId)) {
            console.log(`⚠️ Already tracking upload ${uploadId}, stopping previous tracker`);
            this.stopPlayTracking(uploadId);
        }

        const startTime = Date.now();
        let lastTrackedTime = 0;
        
        const tracker = {
            uploadId,
            startTime,
            mediaElement,
            intervalId: null,
            hasTrackedInitialPlay: false
        };

        // Track initial play ONCE
        this.trackPlay(uploadId, 0, false);
        tracker.hasTrackedInitialPlay = true;

        // Track progress every 30 seconds (reduced frequency to prevent spam)
        tracker.intervalId = setInterval(() => {
            if (mediaElement && !mediaElement.paused) {
                const currentTime = mediaElement.currentTime || 0;
                // Only track significant progress (30 second intervals)
                if (currentTime > lastTrackedTime + 30) {
                    this.trackPlay(uploadId, currentTime, false);
                    lastTrackedTime = currentTime;
                }
            }
        }, 30000);

        // Track completion
        const handleEnded = () => {
            const duration = mediaElement.duration || 0;
            this.trackPlay(uploadId, duration, true);
            this.stopPlayTracking(uploadId);
        };

        // Track when user leaves/pauses
        const handlePause = () => {
            if (mediaElement) {
                const currentTime = mediaElement.currentTime || 0;
                this.trackPlay(uploadId, currentTime, false);
            }
        };

        mediaElement.addEventListener('ended', handleEnded);
        mediaElement.addEventListener('pause', handlePause);

        tracker.cleanup = () => {
            mediaElement.removeEventListener('ended', handleEnded);
            mediaElement.removeEventListener('pause', handlePause);
        };

        this.playTrackers.set(uploadId, tracker);
        return tracker;
    }

    // Stop tracking a specific media play
    stopPlayTracking(uploadId) {
        const tracker = this.playTrackers.get(uploadId);
        if (tracker) {
            if (tracker.intervalId) {
                clearInterval(tracker.intervalId);
            }
            if (tracker.cleanup) {
                tracker.cleanup();
            }
            this.playTrackers.delete(uploadId);
        }
    }

    // Get media statistics
    async getMediaStats(uploadId) {
        try {
            const response = await fetch(`/api/tracking/media/${uploadId}/stats`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching media stats:', error);
            return null;
        }
    }

    // Get profile statistics
    async getProfileStats(username) {
        try {
            const response = await fetch(`/api/tracking/profile/${username}/stats`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching profile stats:', error);
            return null;
        }
    }

    // Cleanup all trackers (call on page unload)
    cleanup() {
        this.playTrackers.forEach((tracker, uploadId) => {
            this.stopPlayTracking(uploadId);
        });
    }
}

// Create singleton instance
const trackingService = new TrackingService();

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    trackingService.cleanup();
});

export default trackingService;